import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import PostCard from '../components/PostCard';
import CommunityActivityForm from '../components/CommunityActivityForm';
import './Communities.css';

function normalizeCommunity(community) {
  if (!community) return community;
  return {
    ...community,
    isMember: community.isMember ?? community.member ?? false,
    isOwner: community.isOwner ?? community.owner ?? false
  };
}

function Communities() {
  const [communities, setCommunities] = useState([]);
  const [myCommunities, setMyCommunities] = useState([]);
  const [selected, setSelected] = useState(null);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [activeTab, setActiveTab] = useState('browse');
  const [showPostForm, setShowPostForm] = useState(false);

  const fetchCommunities = useCallback(async () => {
    try {
      setLoadError('');
      const [allRes, mineRes] = await Promise.all([
        axios.get('/api/communities'),
        axios.get('/api/communities/mine')
      ]);
      setCommunities((allRes.data || []).map(normalizeCommunity));
      setMyCommunities((mineRes.data || []).map(normalizeCommunity));
    } catch (error) {
      console.error('Error fetching communities:', error);
      setCommunities([]);
      setMyCommunities([]);
      if (error.response?.status === 401 || error.response?.status === 403) {
        setLoadError('Session expired or not logged in. Please log in again.');
      } else {
        setLoadError('Could not load communities. Please refresh the page.');
      }
    }
  }, []);

  const fetchCommunityFeed = useCallback(async (communityId) => {
    try {
      const res = await axios.get(`/api/communities/${communityId}/feed`);
      setFeed((res.data || []).filter(item => item.itemType === 'POST' && item.post?.postType === 'ARTICLE'));
    } catch (error) {
      setFeed([]);
      if (error.response?.status === 400 || error.response?.status === 403) {
        alert(error.response?.data?.message || 'Join the community to view content');
      }
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchCommunities();
      setLoading(false);
    };
    init();
  }, [fetchCommunities]);

  const openCommunity = async (community) => {
    const res = await axios.get(`/api/communities/${community.id}`);
    setSelected(normalizeCommunity(res.data));
    if (res.data.isMember) {
      await fetchCommunityFeed(community.id);
    } else {
      setFeed([]);
    }
  };

  const handleJoin = async (communityId) => {
    try {
      const res = await axios.post(`/api/communities/${communityId}/join`);
      await fetchCommunities();
      if (selected?.id === communityId) {
        setSelected(normalizeCommunity(res.data));
        await fetchCommunityFeed(communityId);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to join');
    }
  };

  const handleLeave = async (communityId) => {
    try {
      const res = await axios.post(`/api/communities/${communityId}/leave`);
      await fetchCommunities();
      if (selected?.id === communityId) {
        setSelected(normalizeCommunity(res.data));
        setFeed([]);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to leave');
    }
  };

  const refreshFeed = () => {
    if (selected?.id) fetchCommunityFeed(selected.id);
  };

  const list = activeTab === 'mine' ? myCommunities : communities;

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="communities-container">
      <div className="communities-layout">
        <aside className="communities-sidebar">
          <div className="communities-header">
            <h2>Communities</h2>
            <p>Join to post and see community-only content.</p>
          </div>

          <div className="community-tabs">
            <button className={activeTab === 'browse' ? 'active' : ''} onClick={() => setActiveTab('browse')}>Browse</button>
            <button className={activeTab === 'mine' ? 'active' : ''} onClick={() => setActiveTab('mine')}>My Communities</button>
          </div>

          {loadError && <p className="community-error">{loadError}</p>}

          <div className="community-list">
            {list.length === 0 ? (
              <p className="empty-msg">
                {activeTab === 'mine'
                  ? 'You have not joined any community yet. Browse and join one.'
                  : 'No communities available yet.'}
              </p>
            ) : list.map(c => (
              <div
                key={c.id}
                className={`community-item ${selected?.id === c.id ? 'selected' : ''}`}
                onClick={() => openCommunity(c)}
              >
                <div className="community-item-name">{c.name}</div>
                <div className="community-item-meta">
                  {c.ownerUsername ? `by ${c.ownerUsername} · ` : ''}{c.memberCount} members
                </div>
              </div>
            ))}
          </div>
        </aside>

        <main className="community-detail">
          {!selected ? (
            <div className="community-placeholder">
              <h3>Select a community</h3>
              <p>Join a community to share and discuss articles.</p>
            </div>
          ) : (
            <>
              <div className="community-detail-header">
                <div>
                  <h2>{selected.name}</h2>
                  {selected.description && <p>{selected.description}</p>}
                  <div className="community-detail-meta">
                    {selected.ownerUsername ? `Owned by ${selected.ownerUsername} · ` : ''}
                    {selected.memberCount} members
                  </div>
                </div>
                <div className="community-actions">
                  {selected.isMember ? (
                    <>
                      <button className="btn-primary" onClick={() => setShowPostForm(!showPostForm)}>
                        {showPostForm ? 'Cancel' : '+ Post'}
                      </button>
                      {!selected.isOwner && (
                        <button className="btn-secondary" onClick={() => handleLeave(selected.id)}>Leave</button>
                      )}
                    </>
                  ) : (
                    <button className="btn-primary" onClick={() => handleJoin(selected.id)}>Join (+3 pts)</button>
                  )}
                </div>
              </div>

              {!selected.isMember ? (
                <div className="join-prompt">
                  Join this community to view articles from members.
                </div>
              ) : (
                <>
                  {showPostForm && (
                    <CommunityActivityForm
                      communityId={selected.id}
                      onCreated={() => {
                        setShowPostForm(false);
                        refreshFeed();
                      }}
                    />
                  )}

                  <div className="community-posts">
                    {feed.length === 0 ? (
                      <p className="empty-msg">No activity yet. Be the first to post!</p>
                    ) : feed.map(item => (
                      <PostCard
                        key={`post-${item.post.id}`}
                        post={item.post}
                        onDelete={() => refreshFeed()}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default Communities;
